/**
 * What the renderer needs to know about an encounter, from one seat in it.
 *
 * Split out of `sim-runner.ts` when multiplayer arrived, because "which
 * aeroplane am I?" stopped being a constant. Single-player is always
 * `PLAYER_ID`; in a duel the same encounter is stepped on both machines with
 * the same roster (`host`, `guest`) and each peer draws it from its own seat —
 * so *whose* aeroplane is hidden, whose side counts as friendly, and whose
 * rounds are drawn in the player's colour all become a parameter.
 *
 * The two runners keep their own tick loops (they genuinely differ: one steps
 * on a wall clock, the other on a network ledger) and share every line of
 * this.
 */

import {
  aircraftById,
  bakedModelIdOf,
  headingAngle,
  interpolatePose,
  poseOf,
  targetById,
  targetProfileOf,
  type AircraftStatus,
  type EncounterState,
  type EncounterTarget,
  type Pose,
  type SimState,
  type Vec3,
} from "@red-opus/engine";
import { airframeDamageFraction } from "./smoke-trail.ts";
import { propellerRpmFraction } from "./propeller-rpm.ts";
import type { BurstKind } from "./effect-sprites.ts";

/** One other aircraft, as the renderer needs it. */
export interface AircraftView {
  readonly id: string;
  readonly pose: Pose;
  readonly status: AircraftStatus;
  readonly burning: boolean;
  /** 0..1 — drives the smoke trail before the fire starts. */
  readonly engineHealth: number;
  /** 0..1 across the whole airframe — the smoke trail's own trigger (`smoke-trail.ts`). */
  readonly damageFraction: number;
  /**
   * 0..1 propeller rate, for AD-012's disc — derived here for the same reason
   * `damageFraction` is: it is a pure read of this aeroplane's `SimState` that
   * every view of it wants, and computing it once beside the pose keeps the
   * renderer from carrying three engine fields around to redo it.
   *
   * Optional because the effect it feeds is a *cosmetic* port extension, and
   * nothing that consumes an `AircraftView` for any other purpose should have
   * to supply it. `aircraftViewsOf` always fills it; a view assembled by hand
   * (a test fixture) that leaves it out simply draws a stopped propeller.
   */
  readonly propellerRpm?: number;
  /**
   * World velocity in m/s. Optional for the same reason `propellerRpm` is: the
   * only thing that reads it is a *cosmetic* port extension — ENHANCED EFFECTS'
   * particles, where a flame slips behind the airframe that made it and a puff
   * is left standing in the air. A view assembled by hand without it simply
   * emits into still air.
   *
   * Taken from the current tick rather than differenced across the interpolated
   * poses, which would be the same number with a frame of lag and a divide by a
   * `dt` this function does not have.
   */
  readonly velocity?: Vec3;
  /**
   * Cumulative rounds this aeroplane has taken from another aeroplane's guns —
   * `CombatAircraft.gunHitsTaken`, archie excluded, the same counter the
   * painted cockpit's own bullet holes are ordinaled off (`cockpit-hits.ts`).
   *
   * Optional, and cosmetic: the particle effects spark the frame-to-frame
   * difference. It is a *count*, not a place — the sim resolves an exact impact
   * point inside `combat/collide.ts` and discards it before the tick returns, so
   * there is nothing better to spark from. See `particle-effects.ts`.
   */
  readonly gunHitsTaken?: number;
  /**
   * Rounds left this aeroplane's guns on the tick this view was built —
   * `CombatAircraft.roundsFired` differenced across the same `previous`/`current`
   * pair the pose is interpolated between. Not "the trigger is down": a held
   * trigger over a jam or an empty belt fires nothing and reads `false` here,
   * which is the same distinction `frame-signals.ts`'s `gunAudioFiring` draws
   * for the player's own hand.
   *
   * Optional because the only thing that reads it is `enemy-gun-audio.ts`, and
   * a view assembled by hand (a test fixture) that leaves it out simply makes no
   * gunfire. A single-tick gap is harmless to that consumer — it retriggers on
   * the original's own 118 ms cadence, about seven ticks, so the boolean is
   * sampled far faster than it is used.
   */
  readonly firingNow?: boolean;
  /**
   * The baked model to draw it with, so a mission of four different types gets
   * four different aeroplanes rather than four copies of the opponent's.
   * `null` when the airframe has no baked geometry (`bakedModelIdOf`).
   */
  readonly modelId: string | null;
  readonly friendly: boolean;
}

/**
 * A balloon, Zeppelin, raid structure or train, as the renderer needs it.
 *
 * Poses are exact, not interpolated. Nothing here manoeuvres: a factory does not
 * move at all, and a train runs a straight line at 11 m/s — under half a metre
 * per sim tick — so there is nothing for an interpolator to smooth.
 */
export interface TargetView {
  readonly id: string;
  readonly modelId: string;
  readonly position: { readonly x: number; readonly y: number; readonly z: number };
  readonly headingRad: number;
  /**
   * World velocity, m/s: a train's own travel speed along its route (zero once
   * it has arrived), zero for everything that does not run one. ENHANCED
   * EFFECTS needs it so a working locomotive's coal plume can stream behind the
   * moving train the way an aeroplane's trail streams behind it.
   */
  readonly velocity: { readonly x: number; readonly y: number; readonly z: number };
  readonly burning: boolean;
  readonly destroyed: boolean;
  /**
   * It is trailing a *working* plume — steam and coal smoke, not damage. True
   * only for a train that is still running, mirroring the original's class-5
   * constructor, which attaches the plume unconditionally and stops it when the
   * train retires (`s\intel\move.c`). This is how you find one from height.
   */
  readonly smoking: boolean;
}

/**
 * One thing the renderer should set off at a world point this tick.
 *
 * Derived entirely from the encounter's **existing** `CombatEvent` feed
 * (`packages/engine/src/combat/events.ts`) plus the roster it arrived with — no
 * new engine event was needed for effects, because `destroyed` already names
 * the aircraft or target it is about and the same state carries that entity's
 * position. Resolving the site renderer-side also gets it *right*: the wreck is
 * where it fell on the destroying tick, not where it drifts to a frame later.
 */
export interface EffectSpawn {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Which sprite sequence to play. Wrecks are `explosion`; archie is `flakBurst`. */
  readonly kind: BurstKind;
  /**
   * Whether the site keeps smoking afterwards. An aeroplane leaves a burning
   * wreck; a gasbag that reached `destroyed` has already spent its whole
   * `burnSeconds` alight and is gone (`combat/targets.ts`). A flak burst never
   * does — the original's shell has a four-unit fuse and is simply gone.
   */
  readonly plume: boolean;
}

/**
 * The effect sites this tick implies. Three sources, all already in the
 * engine's own state:
 *
 * - `destroyed` events give wreck explosions. A balloon catching fire is a
 *   *sustained* state the renderer reads off `TargetView.burning` every frame
 *   rather than a one-shot, and `onFire`/`outOfControl` on an aeroplane
 *   already drive the damage smoke.
 * - `bombImpacts` — where a bomb went off this tick (`combat/bombs.ts`). Not
 *   events, because an event names an aircraft and a crater names a place; the
 *   engine carries the tick's impacts with their positions instead. A crater
 *   keeps smoking, so it takes a plume; a gasbag that reached `destroyed` has
 *   already burnt out and does not.
 * - `flakBursts` — a per-tick list the ground-fire pass hands over directly; a
 *   shell going off is not an event about an aircraft either, so it never went
 *   through `CombatEvent`.
 */
export function effectSpawnsOf(state: EncounterState): EffectSpawn[] {
  const spawns: EffectSpawn[] = [];
  for (const burst of state.flakBursts) {
    spawns.push({ ...burst.position, kind: "flakBurst", plume: false });
  }
  for (const event of state.events) {
    if (event.kind !== "destroyed") continue;
    const aircraft = aircraftById(state, event.aircraftId);
    if (aircraft) {
      spawns.push({ ...aircraft.sim.position, kind: "explosion", plume: true });
      continue;
    }
    const target = targetById(state, event.aircraftId);
    if (target) spawns.push({ ...target.position, kind: "explosion", plume: false });
  }
  for (const impact of state.bombImpacts) spawns.push({ ...impact.position, kind: "explosion", plume: true });
  return spawns;
}

/** The sim being flown from `playerId`'s seat, falling back to the first on the roster. */
export function playerSimOf(state: EncounterState, playerId: string): SimState {
  const player = state.aircraft.find((entry) => entry.id === playerId) ?? state.aircraft[0];
  if (!player) throw new Error("an encounter must have at least one aircraft");
  return player.sim;
}

/** The three `SimState` fields AD-012's propeller rate is a function of (`propeller-rpm.ts`). */
export function propellerDriveOf(sim: SimState): {
  readonly throttle: number;
  readonly deliveredPowerW: number;
  readonly airspeedMps: number;
} {
  return {
    throttle: sim.engine.throttle,
    deliveredPowerW: sim.engine.deliveredPowerW,
    airspeedMps: sim.readout.airspeedMps,
  };
}

/** The seat's own side, so "friendly" means the same thing to both peers of a duel. */
function sideOf(state: EncounterState, playerId: string): string {
  return state.aircraft.find((entry) => entry.id === playerId)?.side ?? "friendly";
}

/** Everyone but the seat's own aeroplane, interpolated between the last two ticks. */
export function aircraftViewsOf(
  previous: EncounterState,
  current: EncounterState,
  playerId: string,
  alpha: number,
): AircraftView[] {
  const ourSide = sideOf(current, playerId);
  return current.aircraft
    .filter((entry) => entry.id !== playerId)
    .map((entry) => {
      const before = previous.aircraft.find((other) => other.id === entry.id) ?? entry;
      return {
        id: entry.id,
        pose: interpolatePose(poseOf(before.sim), poseOf(entry.sim), alpha),
        status: entry.status,
        burning: entry.fire.burning,
        engineHealth: entry.sim.damage.engine,
        damageFraction: airframeDamageFraction(entry.sim.damage),
        propellerRpm: propellerRpmFraction(propellerDriveOf(entry.sim)),
        velocity: entry.sim.velocity,
        gunHitsTaken: entry.gunHitsTaken,
        firingNow: entry.roundsFired > before.roundsFired,
        modelId: bakedModelIdOf(entry.sim.config.aircraft),
        friendly: entry.side === ourSide,
      };
    });
}

function targetViewOf(target: EncounterTarget, modelId: string): TargetView {
  return {
    id: target.id,
    modelId,
    position: target.position,
    // A tethered balloon and a Zeppelin both sit wings-level on one heading, so
    // the renderer only needs the yaw — no quaternion interpolation required.
    // Taken from the orientation rather than the velocity, which is zero for a
    // tethered balloon and straight down for a burning one.
    headingRad: headingAngle(target.orientation),
    velocity: target.velocity,
    burning: target.burning,
    destroyed: target.destroyed,
    // A retired train has stopped and its fire is banked; a burning one is on
    // the fire path already and does not need a second plume.
    smoking:
      targetProfileOf(target.kind).emitsSmoke && !target.destroyed && !target.burning && !target.arrived,
  };
}

/**
 * The targets the fleet renderer draws.
 *
 * A kind with no `bakedModelId` is skipped rather than faked: the searchlight
 * posts (AD-017 §11) are on the roster and are shot at like anything else, but
 * their hardware is the enhanced projector `scene-searchlights.ts` instances
 * with its own articulation, and handing `combat-fleet.ts` a model id that has
 * no `data/baked/models/*.json` behind it would be asking `aircraft-mesh.ts`
 * for a file that does not exist. Filtering here also keeps them off every
 * count that runs through this view — the HUD's target list, the raid rows —
 * where a lamp was never meant to appear.
 */
export function targetViewsOf(state: EncounterState): TargetView[] {
  const views: TargetView[] = [];
  for (const target of state.targets) {
    const modelId = targetProfileOf(target.kind).bakedModelId;
    if (modelId !== null) views.push(targetViewOf(target, modelId));
  }
  return views;
}

/** The seat's own pose to draw: interpolated between the last two sim states. */
export function playerPoseOf(
  previous: EncounterState,
  current: EncounterState,
  playerId: string,
  alpha: number,
): Pose {
  return interpolatePose(poseOf(playerSimOf(previous, playerId)), poseOf(playerSimOf(current, playerId)), alpha);
}
